import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../store/authContext';
import { dashboardApi } from '../../services/api';
import logger from '../../utils/logger';
import './SuperAdminDashboard.css';

export default function SuperAdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchStats = useCallback(async () => {
    try {
      setError('');
      const res = await dashboardApi.platformStats();
      setStats(res.data);
    } catch (err) {
      logger.error('api', 'Failed to load platform stats', { error: err.message });
      setError('Failed to load platform stats.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  if (loading) {
    return (
      <div className="sa-dashboard">
        <div className="sa-loading">
          <div className="sa-loading-spinner" />
          <p>Loading dashboard…</p>
        </div>
      </div>
    );
  }

  const greeting = getGreeting();

  return (
    <div className="sa-dashboard">
      {/* Header */}
      <div className="page-header animate-fade-up">
        <div className="page-header-top">
          <div>
            <p className="sa-greeting">{greeting}</p>
            <h1 className="page-title">{user?.name || 'Dashboard'}</h1>
          </div>
          <div className="page-actions">
            <Link to="/super-admin/tenants" className="btn btn-ghost btn-sm">
              View Organisations
            </Link>
          </div>
        </div>
        <p className="page-subtitle">
          {formatDate(new Date())} &middot; Platform Overview
        </p>
      </div>

      {error && <div className="sa-error animate-fade-up">{error}</div>}

      {stats && (
        <>
          {/* Stat cards */}
          <div className="sa-stats stagger">
            <StatCard
              label="Total Organisations"
              value={stats.total_orgs}
              icon={<BuildingIcon />}
              className="animate-fade-up"
            />
            <StatCard
              label="Total Users"
              value={stats.total_users}
              icon={<UsersCountIcon />}
              accent
              className="animate-fade-up"
            />
          </div>

          {/* Organisations table */}
          <div className="animate-fade-up" style={{ animationDelay: '120ms' }}>
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div className="card-head" style={{ padding: '18px 16px 0' }}>
                <h3 className="card-title">Organisations</h3>
                <p className="card-desc">{stats.orgs.length} on the platform</p>
              </div>
              <div style={{ overflowX: 'auto' }}>
              <table className="sa-orgs-table">
                <thead>
                  <tr>
                    <th>Organisation</th>
                    <th>Users</th>
                    <th>Modules</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.orgs.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="sa-empty">
                        No organisations yet.
                      </td>
                    </tr>
                  ) : (
                    stats.orgs.map((org) => (
                      <tr
                        key={org.id}
                        className="sa-orgs-row"
                        onClick={() => navigate(`/super-admin/tenants/${org.id}`)}
                      >
                        <td>
                          <div className="sa-org-name-cell">
                            <div className="sa-org-avatar">
                              {org.name.split(' ').map((w) => w[0]).slice(0, 2).join('')}
                            </div>
                            <span className="sa-org-name">{org.name}</span>
                          </div>
                        </td>
                        <td className="sa-user-count">{org.user_count}</td>
                        <td>
                          <div className="sa-module-badges">
                            {org.leads_module_enabled && (
                              <span className="sa-module-badge sa-module-badge--leads">Leads</span>
                            )}
                            {org.booking_module_enabled && (
                              <span className="sa-module-badge sa-module-badge--booking">Booking</span>
                            )}
                            {org.sales_module_enabled && (
                              <span className="sa-module-badge sa-module-badge--sales">Sales</span>
                            )}
                            {!org.leads_module_enabled && !org.booking_module_enabled && !org.sales_module_enabled && (
                              <span className="sa-module-badge sa-module-badge--none">None</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <span className={`badge badge-${org.is_active ? 'closed' : 'lost'}`}>
                            <span className="badge-dot" />
                            {org.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ─── Sub-components ─────────────────────────────────────────────────────── */

function StatCard({ label, value, icon, accent, className }) {
  return (
    <div className={`stat-card ${accent ? 'stat-card--accent' : ''} ${className || ''}`}>
      <div className="stat-card-top">
        <div className="stat-card-icon">{icon}</div>
      </div>
      <div className="stat-card-value">{value}</div>
      <div className="stat-card-label">{label}</div>
    </div>
  );
}

/* ─── Utils ──────────────────────────────────────────────────────────────── */

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning,';
  if (h < 17) return 'Good afternoon,';
  return 'Good evening,';
}

function formatDate(date) {
  return date.toLocaleDateString('en-IN', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
}

/* ─── Icons ──────────────────────────────────────────────────────────────── */

function BuildingIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="2" y="4" width="8" height="11" rx="1" stroke="currentColor" strokeWidth="1.4" />
      <rect x="10" y="7" width="4" height="8" rx="1" stroke="currentColor" strokeWidth="1.4" />
      <path d="M5 7.5h2M5 10h2M5 12.5h2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <path d="M12 10h0" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function UsersCountIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="6" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.4" />
      <path d="M1 13.5C1 11.015 3.239 9 6 9s5 2.015 5 4.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="11.5" cy="5" r="2" stroke="currentColor" strokeWidth="1.3" />
      <path d="M13.5 13C13.5 11.343 12.657 9.9 11.5 9.2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}
